import React, { useState, useEffect } from "react";
import { Table, Select, DatePicker, Input, Upload, Button, Tooltip, Typography, Tag, message } from "antd";
import {
  CheckCircleTwoTone, CloseCircleTwoTone, EditOutlined, DownOutlined,
  CalendarOutlined, UploadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  FIELD_DEFS, FIELD_TYPE, DOWNSTREAM_FIELDS, optionsForField,
  dateBoundsForField, datesInvalidatedBy,
  missingParentsFor, parentScopeSummary, labelsFor, joinLabels, PARENT_FIELDS,
  FIELD_DEF_BY_FIELD,
} from "./fieldDefs";

const { Text } = Typography;
const { Option } = Select;

export const cellKey = (rowId, field) => `${rowId}::${field}`;
export const cellDomId = (rowId, field) => `hob-cell-${rowId}-${field}`;

const ERROR_BORDER = "1px solid #ff4d4f";
const DIRTY_BORDER = "1px dashed #faad14";

// Day granularity only — these are plain dates with no time component, so
// both sides truncate to the calendar day before comparing.
const isBeforeDay = (a, b) => moment(a).isBefore(moment(b), "day");

/**
 * What an empty dropdown says. "Fill in the columns this one depends on
 * first" was both vague and often untrue — a scoped list can come back empty
 * with every parent already filled, and then the real answer is that nothing
 * exists for that combination, which is a different problem needing a
 * different fix. Name the actual columns in the first case, and the actual
 * combination in the second.
 */
function emptyListMessage(def, row, options, masterData) {
  if (options.length) return "No match";

  const missing = missingParentsFor(def.field, row);
  if (missing.length) {
    return `Fill in ${joinLabels(labelsFor(missing))} first`;
  }

  const scope = parentScopeSummary(def.field, row);
  if (!scope) return `No ${def.label} options available`;

  // Every parent is filled and the list is still empty, so at least one of
  // them is over-constraining the row. Work out, per parent, which of its
  // values would actually yield something — then advise on whichever has the
  // fewest, since that's the narrowest, most concrete fix. (Okra exists only
  // as "Loose": changing Sample Variant has exactly one answer, while
  // changing Commodity has a dozen, so Sample Variant is the better advice
  // even though Commodity is equally "a" blocker.)
  const candidates = (PARENT_FIELDS[def.field] || [])
    .map((parent) => {
      const parentDef = FIELD_DEF_BY_FIELD[parent];
      if (!parentDef) return null;
      const workable = optionsForField(parentDef, { ...row, [parent]: "" }, masterData)
        .filter((value) =>
          optionsForField(def, { ...row, [parent]: value }, masterData).length > 0);
      return workable.length ? { parentDef, workable } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.workable.length - b.workable.length);

  if (!candidates.length) return `No ${def.label} exists for ${scope}`;

  const best = candidates[0];
  return best.workable.length <= 3
    ? `No ${def.label} for ${scope} — set ${best.parentDef.label} to ${best.workable.join(" or ")}`
    : `No ${def.label} for ${scope} — check ${best.parentDef.label}`;
}

/**
 * One grid cell. It renders as plain text until clicked, and only then swaps
 * in the real control — with ~50 columns per row, always-mounted inputs make
 * the table crawl, and this keeps the DOM light while still being editable
 * everywhere.
 *
 * The affordance also says what kind of cell it is at a glance, which is the
 * point: a caret for anything picked from a list, a pencil for anything
 * typed by hand, a calendar for dates.
 */
function GridCell({ def, row, masterData, errors, dirty, focused, onChange }) {
  const [editing, setEditing] = useState(false);
  const value = row[def.field];
  const hasError = !!(errors && errors.length);

  // Dirty wins over error on purpose: once a flagged cell is edited, the old
  // red verdict no longer describes what's in it. Amber says "changed, not
  // re-checked yet"; it goes back to red only if Re-validate still rejects it.
  const wrapperStyle = {
    minHeight: 30,
    padding: "3px 6px",
    borderRadius: 4,
    cursor: def.type === FIELD_TYPE.FILE ? "default" : "pointer",
    border: dirty ? DIRTY_BORDER : hasError ? ERROR_BORDER : "1px solid transparent",
    background: dirty ? "#fffbe6" : hasError ? "#fff1f0" : "transparent",
    boxShadow: focused ? "0 0 0 2px #1890ff" : undefined,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
  };

  if (def.type === FIELD_TYPE.FILE) {
    const file = row[def.field];
    return (
      <div id={cellDomId(row.rowId, def.field)} style={wrapperStyle}>
        <div style={{ minWidth: 0 }}>
          {file ? (
            <Tooltip title={file.name}>
              <Tag style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "bottom", marginRight: 0 }}>
                {file.name}
              </Tag>
            </Tooltip>
          ) : (
            <Tooltip title="No file attached — use the upload button">
              <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
            </Tooltip>
          )}
        </div>
        <Upload showUploadList={false} beforeUpload={(f) => { onChange(f); return false; }}>
          <Button size="small" type="link" icon={<UploadOutlined />} style={{ padding: 0 }} />
        </Upload>
      </div>
    );
  }

  if (editing) {
    const stop = () => setEditing(false);

    if (def.type === FIELD_TYPE.MASTER || def.type === FIELD_TYPE.SELECT) {
      const options = optionsForField(def, row, masterData);
      return (
        <Select
          size="small"
          autoFocus
          defaultOpen
          showSearch
          allowClear
          optionFilterProp="children"
          style={{ width: "100%" }}
          value={value || undefined}
          placeholder={`Select ${def.label}`}
          onBlur={stop}
          onChange={(next) => { onChange(next === undefined ? "" : next); stop(); }}
          notFoundContent={emptyListMessage(def, row, options, masterData)}
        >
          {options.map((option) => <Option key={option} value={option}>{option}</Option>)}
        </Select>
      );
    }

    if (def.type === FIELD_TYPE.DATE) {
      // Bounded by whatever stages this row already has filled in, so an
      // out-of-order date is simply not selectable — same restriction the
      // single-order form applies, rather than letting it be picked and
      // bounced by the server afterwards.
      const { min, max } = dateBoundsForField(def.field, row);
      return (
        <DatePicker
          size="small"
          autoFocus
          open
          style={{ width: "100%" }}
          value={value ? moment(value) : null}
          disabledDate={(current) => {
            if (!current) return false;
            if (min && current.isBefore(moment(min), "day")) return true;
            if (max && current.isAfter(moment(max), "day")) return true;
            return false;
          }}
          onChange={(date) => { onChange(date ? date.format("YYYY-MM-DD") : ""); stop(); }}
          onOpenChange={(open) => { if (!open) stop(); }}
        />
      );
    }

    return (
      <Input
        size="small"
        autoFocus
        type={def.type === FIELD_TYPE.NUMBER ? "number" : "text"}
        defaultValue={value}
        onBlur={(e) => { onChange(e.target.value); stop(); }}
        onPressEnter={(e) => { onChange(e.target.value); stop(); }}
      />
    );
  }

  const icon = def.type === FIELD_TYPE.MASTER || def.type === FIELD_TYPE.SELECT
    ? <DownOutlined style={{ fontSize: 10, color: "#bfbfbf" }} />
    : def.type === FIELD_TYPE.DATE
      ? <CalendarOutlined style={{ fontSize: 11, color: "#bfbfbf" }} />
      : <EditOutlined style={{ fontSize: 11, color: "#bfbfbf" }} />;

  const display = (
    <div id={cellDomId(row.rowId, def.field)} style={wrapperStyle} onClick={() => setEditing(true)}>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }}>
        {value || <Text type="secondary" style={{ fontSize: 12 }}>—</Text>}
      </span>
      {icon}
    </div>
  );

  if (dirty) {
    return (
      <Tooltip title={hasError
        ? `Was: ${errors.map((e) => e.message).join(" · ")} — edited, press Re-validate to re-check`
        : "Edited — press Re-validate to re-check"}>
        {display}
      </Tooltip>
    );
  }
  return hasError
    ? <Tooltip title={errors.map((e) => e.message).join(" · ")}>{display}</Tooltip>
    : display;
}

export default function ReviewStep({ rows, onChangeRows, masterData, errorsByCell, dirtyCells, rowStatus, focusedCell }) {
  // Clicking an error in the list below points here — with ~50 columns the
  // offending cell is almost always scrolled out of view, so jumping to it is
  // the difference between a useful error list and one you have to hunt with.
  useEffect(() => {
    if (!focusedCell) return;
    const element = document.getElementById(cellDomId(focusedCell.rowId, focusedCell.field));
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [focusedCell]);

  const updateCell = (rowId, field, value) => {
    // Every control's onChange can fire on its way out even when nothing was
    // actually typed or picked — a text input fires onBlur unconditionally,
    // so opening the editor and clicking away without typing looks the same
    // to it as an edit. Only a real value change should count as "dirty".
    let changedFields = null;
    const nextRows = rows.map((row) => {
      if (row.rowId !== rowId) return row;
      if ((row[field] || "") === (value || "")) return row;
      const next = { ...row, [field]: value };
      const touched = [field];
      // Clearing what a changed parent scoped keeps the row from silently
      // holding a combination the server already rejected once.
      (DOWNSTREAM_FIELDS[field] || []).forEach((downstream) => {
        if (next[downstream]) {
          next[downstream] = "";
          touched.push(downstream);
        }
      });
      // A date moving later can strand ones further down the chain that were
      // valid when they were picked. Clear them rather than leave the row
      // holding an order the server will reject anyway — and say so, since
      // silently emptying cells the user didn't touch would be worse.
      const stranded = datesInvalidatedBy(field, value, next, isBeforeDay);
      stranded.forEach((key) => {
        next[key] = "";
        touched.push(key);
      });
      if (stranded.length) {
        const rowNumber = rows.findIndex((r) => r.rowId === rowId) + 1;
        message.warning(
          `Row ${rowNumber}: cleared ${stranded.length} date${stranded.length > 1 ? "s" : ""} that are now out of order — please re-enter them.`
        );
      }
      changedFields = touched;
      return next;
    });
    if (!changedFields) return;
    onChangeRows(nextRows, { rowId, fields: changedFields });
  };

  const columns = [
    {
      title: "#",
      key: "index",
      fixed: "left",
      width: 48,
      render: (_, __, index) => <Text type="secondary" style={{ fontSize: 12 }}>{index + 1}</Text>,
    },
    {
      title: "Status",
      key: "status",
      fixed: "left",
      width: 70,
      render: (_, row) => {
        const status = rowStatus ? rowStatus[row.rowId] : null;
        if (!status || status.count === undefined) {
          return <Tooltip title="Not validated yet"><Text type="secondary">—</Text></Tooltip>;
        }
        if (status.count === 0) {
          return <Tooltip title="No errors"><CheckCircleTwoTone twoToneColor="#52C41A" style={{ fontSize: 16 }} /></Tooltip>;
        }
        return (
          <Tooltip title={`${status.count} error${status.count > 1 ? "s" : ""}`}>
            <span><CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ fontSize: 16 }} /> <Text type="danger" style={{ fontSize: 12 }}>{status.count}</Text></span>
          </Tooltip>
        );
      },
    },
    ...FIELD_DEFS.map((def) => ({
      title: def.label,
      key: def.field,
      width: def.width,
      render: (_, row) => (
        <GridCell
          def={def}
          row={row}
          masterData={masterData}
          errors={errorsByCell ? errorsByCell[cellKey(row.rowId, def.field)] : null}
          dirty={dirtyCells ? dirtyCells.has(cellKey(row.rowId, def.field)) : false}
          focused={!!focusedCell && focusedCell.rowId === row.rowId && focusedCell.field === def.field}
          onChange={(value) => updateCell(row.rowId, def.field, value)}
        />
      ),
    })),
  ];

  const totalWidth = FIELD_DEFS.reduce((sum, def) => sum + def.width, 120);

  return (
    <>
      <Text type="secondary">
        Every column is editable here — click a cell to change it. A caret means the value comes
        from a list, a pencil means it's typed by hand. Cells the server flagged are outlined in
        red; editing one turns it amber until Re-validate confirms the fix. Click any error in the
        list below to jump straight to its cell.
      </Text>
      <style>{`
        .hob-review-scroll .ant-table-cell { padding: 4px 6px !important; }
        .hob-review-scroll .ant-table-thead > tr > th { font-size: 12px; padding: 6px !important; white-space: nowrap; }
        .hob-review-scroll .ant-table-content, .hob-review-scroll .ant-table-body { scrollbar-width: thin; scrollbar-color: rgba(10, 128, 128, 0.35) transparent; }
        .hob-review-scroll .ant-table-content::-webkit-scrollbar, .hob-review-scroll .ant-table-body::-webkit-scrollbar { height: 8px; width: 8px; }
        .hob-review-scroll .ant-table-content::-webkit-scrollbar-thumb, .hob-review-scroll .ant-table-body::-webkit-scrollbar-thumb { background: rgba(10, 128, 128, 0.35); border-radius: 4px; }
        .hob-review-scroll .ant-table-content::-webkit-scrollbar-track, .hob-review-scroll .ant-table-body::-webkit-scrollbar-track { background: transparent; }
      `}</style>
      <Table
        className="hob-review-scroll"
        style={{ marginTop: 12 }}
        size="small"
        rowKey="rowId"
        dataSource={rows}
        columns={columns}
        pagination={false}
        scroll={{ x: totalWidth, y: 300 }}
      />
    </>
  );
}
